import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link, withRouter } from "react-router-dom";
import styled, { withTheme } from "styled-components";
import {
    HomeRounded,
    AssessmentOutlined,
    SyncAltRounded,
    DescriptionRounded,
    MenuBook
} from "@mui/icons-material";
import { connect } from "react-redux";

import { LargeOnly } from "commons";
import { getDefaultAnalysisDateRange, getDefaultDocumentsDateRange } from "commons/src/NuDateUtils";
import { isTransactionsEnabled } from "commons/src/helpers/RoleHelpers";
import { isLandscape, isMobileOS, isSmallScreen } from "commons/src/helpers/ContextHelpers";
import { isInsideGoReactNativeApp } from "commons/src/helpers/ReactNativeAppHelpers";
import { NUCLEUS_ADMIN_LOCATION_ID } from "commons/src/constants/adminConstants";
import { CLIENT_LIBRARY_URL } from "commons/src/constants/urlConstants";

import { UserDetails } from "./UserDetails";
import { createRafPolyfill } from "../helpers/RequestAnimationHandlerPolyfill";
import { routes } from "../helpers/RouteHelpers";

const HOME = "home";
const ANALYSIS = "analysis";
const TRANSACTIONS = "transactions";
const DOCUMENTS = "documents";
const LIBRARY = "library";
const DEFAULT_RANGE = getDefaultAnalysisDateRange().id;
const DEFAULT_DOCUMENTS_RANGE = getDefaultDocumentsDateRange().id;
const NOTIFICATION_SIZE = 14;

if (!window.requestAnimationFrame) {
    createRafPolyfill();
}

const HIDDEN_FOOTER_TRANSLATE_Y = 98;

export class NavBarComponent extends Component {
    state = {
        hidden: false,
        lastScrollTop: 0,
        minimalView: false,
        ticking: false
    };

    componentDidMount() {
        window.addEventListener("resize", this.resizeHandler);
        window.addEventListener("scroll", this.scrollHandler);
        this.resizeHandler();
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.scrollHandler);
        window.removeEventListener("resize", this.resizeHandler);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.state.hidden !== nextState.hidden ||
            this.state.minimalView !== nextState.minimalView ||
            this.props.location.pathname !== nextProps.location.pathname ||
            this.props.unreadDocumentsPresent !== nextProps.unreadDocumentsPresent
        );
    }

    hasScrolled = scrollTop => {
        const minScrollThreshold = 80;
        return scrollTop > minScrollThreshold && scrollTop > this.state.lastScrollTop;
    };

    resizeHandler = () => {
        const minimalView =
            isMobileOS.iOS() && isLandscape() && isSmallScreen(this.props.theme.maxWidth);
        this.setState({ minimalView });
        this.scrollHandler();
    };

    scrollHandler = () => {
        if (!this.state.ticking) {
            this.setState({ ticking: true });
            window.requestAnimationFrame(() => {
                if (!this.state.minimalView) {
                    const lastScrollTop = window.scrollY || document.documentElement.scrollTop;
                    const hidden = this.hasScrolled(lastScrollTop);
                    this.setState({ hidden, lastScrollTop, ticking: false });
                } else {
                    this.setState({ hidden: false, ticking: false });
                }
            });
        }
    };

    isActive = (pathname, target) => pathname.includes(target);

    render() {
        const isTransEnabled = isTransactionsEnabled(this.props.roles);

        const { pathname } = this.props.location;

        if (isInsideGoReactNativeApp()) {
            return null;
        }

        const adviserLocationId = this.props.adviserLocationId;

        const isOrphanClient = adviserLocationId === NUCLEUS_ADMIN_LOCATION_ID ? true : false;

        return (
            <StyledDiv>
                <Footer
                    hidden={this.state.hidden}
                    minimalView={this.state.minimalView}
                    data-testid="navbar-footer"
                >
                    <Nav role="navigation" aria-label="main" items={isTransEnabled ? 4 : 3}>
                        <LargeOnly>
                            <UserDetails isSideBar={false} hideHeader={true} />
                        </LargeOnly>

                        <Link to={routes.client.home()} data-testid="navigation-link">
                            <IconContainer isActive={this.isActive(pathname, HOME)}>
                                <HomeRounded aria-hidden="true" />
                            </IconContainer>
                            <MenuText hidden={this.state.minimalView} data-testid="nav-menu-text">
                                Home
                            </MenuText>
                        </Link>
                        <Link
                            to={routes.client.analysis({
                                accountNum: "default",
                                rangeId: DEFAULT_RANGE
                            })}
                            data-testid="navigation-link"
                        >
                            <IconContainer isActive={this.isActive(pathname, ANALYSIS)}>
                                <AssessmentOutlined aria-hidden="true" />
                            </IconContainer>
                            <MenuText hidden={this.state.minimalView} data-testid="nav-menu-text">
                                Analysis
                            </MenuText>
                        </Link>
                        {isTransEnabled && (
                            <Link
                                to={routes.client.transactions({
                                    accountNum: "default",
                                    rangeId: DEFAULT_RANGE
                                })}
                                data-testid="navigation-link"
                            >
                                <IconContainer isActive={this.isActive(pathname, TRANSACTIONS)}>
                                    <SyncAltRounded aria-hidden="true" />
                                </IconContainer>
                                <MenuText
                                    hidden={this.state.minimalView}
                                    data-testid="nav-menu-text"
                                >
                                    Transactions
                                </MenuText>
                            </Link>
                        )}
                        <Link
                            to={routes.client.documents({
                                accountNum: "default",
                                rangeId: DEFAULT_DOCUMENTS_RANGE,
                                documentCategory: "all"
                            })}
                            data-testid="navigation-link"
                        >
                            <IconContainer
                                isActive={this.isActive(pathname, DOCUMENTS)}
                                style={{ position: "relative" }}
                            >
                                <DescriptionRounded aria-hidden="true" />
                                {this.props.unreadDocumentsPresent ? (
                                    <StyledNotificationIcon data-testid="notification-icon">
                                        !
                                    </StyledNotificationIcon>
                                ) : null}
                            </IconContainer>
                            <MenuText hidden={this.state.minimalView} data-testid="nav-menu-text">
                                Documents
                            </MenuText>
                        </Link>
                        {isOrphanClient ? (
                            <a
                                href={CLIENT_LIBRARY_URL}
                                data-testid="library-link"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <IconContainer isActive={this.isActive(pathname, LIBRARY)}>
                                    <MenuBook aria-hidden="true" />
                                </IconContainer>
                                <MenuText
                                    hidden={this.state.minimalView}
                                    data-testid="nav-menu-text"
                                >
                                    Library
                                </MenuText>
                            </a>
                        ) : null}
                    </Nav>
                </Footer>
            </StyledDiv>
        );
    }
}

const Footer = styled.footer`
    background-color: ${props => props.theme.secondary};
    padding: 1em;
    bottom: 0;
    height: auto !important;
    left: 0;
    transform: translateY(
        ${props => {
            if (!props.minimalView) {
                return props.hidden ? HIDDEN_FOOTER_TRANSLATE_Y : 0;
            }
            return 0;
        }}px
    );
    position: fixed;
    transition: transform 0.3s ease-out;
    width: 100%;
    z-index: 3;

    @media (prefers-reduced-motion) {
        transition: none;
    }

    @media (min-width: ${props => props.theme.minWidth}) {
        position: relative;
        width: 250px;
        z-index: 0;
        min-height: calc(100vh - ${props => props.theme.topBarHeightDesktop});
    }

    @media print {
        display: none;
    }
`;

export const MenuText = styled.span`
    color: ${props => props.theme.tertiary};
    font-size: 12px;
    text-align: center;
    visibility: ${props => (props.hidden ? "hidden" : "visible")};

    @media (max-width: ${props => props.theme.maxWidth}) {
        margin-top: 1em;
    }
`;

export const Nav = styled.nav`
    @media (max-width: ${props => props.theme.maxWidth}) {
        display: flex;
    }
`;

export const IconContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${props => (props.isActive ? props.theme.primary : props.theme.tertiary)};
    border-radius: 50%;
    width: 42px;
    height: 42px;

    .MuiSvgIcon-root {
        color: ${props => (props.isActive ? props.theme.tertiary : props.theme.secondary)};
    }

    :hover {
        background: ${props => props.theme.primary};
        .MuiSvgIcon-root {
            color: ${props => props.theme.tertiary};
        }
    }

    @media (min-width: ${props => props.theme.minWidth}) {
        margin-right: 1rem;
    }
`;

const StyledDiv = styled.div`
    display: flex;
    min-height: 100vh;
    a,
    Link {
        align-items: center;
        text-decoration: none;
        width: 100%;
        transition: all 0.25s ease;

        :hover {
            .MuiSvgIcon-root {
                color: ${props => props.theme.tertiary};
            }

            ${IconContainer} {
                background: ${props => props.theme.primary};
            }
        }

        @media (min-width: ${props => props.theme.minWidth}) {
            display: inline-flex;
            margin: 10px 17px 10px 0;
            padding-left: 8%;
            top: 13px;
            text-decoration: none;
        }

        @media (max-width: ${props => props.theme.maxWidth}) {
            display: flex;
            flex-direction: column;
            justify-content: center;
        }
        @media (prefers-reduced-motion) {
            transition: none;
        }
    }
`;

const StyledNotificationIcon = styled.div`
    position: absolute;
    height: ${NOTIFICATION_SIZE}px;
    width: ${NOTIFICATION_SIZE}px;
    background-color: red;
    border-radius: 50%;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 10px;
    font-weight: bold;
`;

NavBarComponent.propTypes = {
    roles: PropTypes.array,
    theme: PropTypes.shape({
        maxWidth: PropTypes.string
    }),
    location: PropTypes.shape({
        pathname: PropTypes.string
    }),
    adviserLocationId: PropTypes.number,
    unreadDocumentsPresent: PropTypes.bool
};

NavBarComponent.defaultProps = {
    theme: {}
};

const mapStateToProps = state => {
    return {
        adviserLocationId: state.adviser?.adviser?.location?.locationId,
        unreadDocumentsPresent: state.documents?.unreadDocumentsPresent
    };
};

export const NavBar = connect(mapStateToProps)(withTheme(withRouter(NavBarComponent)));
